import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element
 *
 * @extends {Component}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    // observe initial-slide (existing) and direction for vertical toggle
    return ['initial-slide', 'direction'];
  }

  requiredRefs = ['scroller'];

  // Private fields (declared before any usage)
  #axis = 'x'; // 'x' or 'y'
  #thumbnailListeners = [];
  #scroll; // Scroller instance
  #resizeObserver;
  #dragging = false;
  #disabled = false;
  #interval = undefined;
  #current = 0;

  // resilient slideshowContainer lookup
  get slideshowContainerEl() {
    return this.refs.slideshowContainer || this.querySelector('.slideshow, .slideshow-container, .product-media-gallery__slideshow');
  }

  // Best-effort scroller lookup:
  // 1) this.refs.scroller
  // 2) parent element that contains the slide elements (keeps per-instance targeting)
  // 3) fallback selectors
  get scrollerEl() {
    if (this.refs.scroller) return this.refs.scroller;

    const slides = this.refs.slides || Array.from(this.querySelectorAll('slideshow-slide'));
    if (slides && slides.length) {
      // find nearest common ancestor that contains all slides
      let parent = slides[0].parentElement;
      while (parent) {
        if (slides.every((s) => parent.contains(s))) return parent;
        parent = parent.parentElement;
      }
    }

    return this.querySelector('.slideshow__scroller, .product-media__slideshow, .product__media-scroller, scroll-hint, [data-scroller]');
  }

  // Apply horizontal / vertical styles to the resolved scroller element only
  #applyHorizontal() {
    const el = this.scrollerEl;
    if (!el) return;
    el.style.flexDirection = 'row';
    el.style.overflowX = 'auto';
    el.style.overflowY = 'hidden';
    el.style.scrollSnapType = 'x mandatory';
    el.classList.remove('slideshow__scroller--vertical');
    el.dataset.axis = 'x';
  }

  #applyVertical() {
    const el = this.scrollerEl;
    if (!el) return;
    el.style.flexDirection = 'column';
    el.style.overflowY = 'auto';
    el.style.overflowX = 'hidden';
    el.style.scrollSnapType = 'y mandatory';
    el.classList.add('slideshow__scroller--vertical');
    el.dataset.axis = 'y';
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // keep existing initial-slide handling
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
      return;
    }

    // handle direction attribute changes (toggle vertical only when explicitly set)
    if (name === 'direction' && oldValue !== newValue) {
      // ensure update runs after attribute change is applied and component connected
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.#updateAxis();
      });
    }
  }

  /* -------------------------
     Helper accessors
     ------------------------- */

  get slides() {
    return Array.from(this.refs.slides || []);
  }

  get initialSlideIndex() {
    const attr = this.getAttribute('initial-slide');
    return attr ? parseInt(attr, 10) || 0 : 0;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  get current() {
    return this.#current;
  }

  set current(value) {
    this.#current = value;
    const { current: currentNode, thumbnails, dots, slides, previous, next } = this.refs;
    if (currentNode) currentNode.textContent = `${value + 1}`;
    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }
    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.hasAttribute('infinite') != null;
  }

  get visibleSlides() {
    const axis = this.#axis || 'x';
    return getVisibleElements(this.scrollerEl, this.slides, SLIDE_VISIBLITY_THRESHOLD, axis);
  }

  get previousIndex() {
    const modifier = this.visibleSlides.length > 1 ? this.visibleSlides.length : 1;
    return this.current - modifier;
  }

  get nextIndex() {
    const modifier = this.visibleSlides.length > 1 ? this.visibleSlides.length : 1;
    return this.current + modifier;
  }

  get atStart() {
    return this.slides.length ? this.current === 0 : false;
  }

  get atEnd() {
    return this.slides.length ? this.current === this.slides.length - 1 : false;
  }

  /* -------------------------
     Private utilities
     ------------------------- */

  // Update aria-hidden based on visibility
  #updateVisibleSlides() {
    const slides = this.slides;
    if (!slides.length) return 0;
    const visibleSlides = this.visibleSlides;
    scheduler.schedule(() => {
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });
    return visibleSlides.length;
  }

  // Create/destroy/recreate scroller when axis changes
  #createScroller(scrollerEl) {
    this.#scroll?.destroy();
    this.#scroll = new Scroller(scrollerEl, {
      axis: this.#axis,
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });
    // sensible defaults
    this.#scroll.snap = true;
  }

  // Handle pointer/drag to allow swiping the scroller.
  #handleMouseDown = (event) => {
    const slides = this.slides;
    if (!slides.length || slides.length <= 1) return;
    if (!(event instanceof PointerEvent)) return;
    if (this.disabled || this.#dragging) return;
    if (event.target instanceof Element && event.target.closest('model-viewer')) return;

    const scroller = this.scrollerEl;
    if (!scroller) return;

    event.preventDefault();
    try { scroller.setPointerCapture?.(event.pointerId); } catch (e) { /* ignore */ }

    const axis = this.#scroll?.axis || 'x';
    const startPos = axis === 'x' ? event.clientX : event.clientY;
    const controller = new AbortController();
    const { signal } = controller;
    let previous = startPos;
    let lastTime = performance.now();
    let velocity = 0;
    let moved = false;
    let distance = 0;
    this.#dragging = true;

    const onPointerMove = (ev) => {
      if (!(ev instanceof PointerEvent)) return;
      const current = axis === 'x' ? ev.clientX : ev.clientY;
      const delta = previous - current;
      if (!delta) return;
      const now = performance.now();
      const dt = Math.max(8, now - lastTime);
      // velocity in px/sec
      velocity = (delta / dt) * 1000;
      lastTime = now;
      previous = current;
      distance += Math.abs(delta);

      if (!moved) {
        moved = true;
        document.addEventListener('click', preventDefault, { once: true, signal });
        this.pause();
        this.setAttribute('dragging', '');
      }
      ev.stopImmediatePropagation();

      if (this.#scroll) {
        this.#scroll.by(delta, { instant: true });
      } else if (axis === 'x') {
        scroller.scrollLeft += delta;
      } else {
        scroller.scrollTop += delta;
      }
    };

    const onPointerUp = async (ev) => {
      controller.abort();
      this.#dragging = false;
      try { scroller.releasePointerCapture?.(ev.pointerId); } catch (e) { /* ignore */ }

      const currentIdx = this.current;
      const snappedIdx = this.#sync();

      // compute total signed movement from startPos -> final pointer (start - previous)
      const total = startPos - previous;
      const size = axis === 'x' ? scroller.clientWidth || scroller.offsetWidth : scroller.clientHeight || scroller.offsetHeight;
      const distanceThreshold = Math.max(40, size * 0.15); // 15% of viewport or min 40px
      const velocityThreshold = 300; // px/sec

      let targetIndex = snappedIdx;
      if (Math.abs(total) > distanceThreshold || Math.abs(velocity) > velocityThreshold) {
        // total > 0 => user swiped left -> advance slide
        const dir = total > 0 ? 1 : -1;
        targetIndex = clamp(currentIdx + dir, 0, slides.length - 1);
      } else {
        targetIndex = snappedIdx;
      }

      const newSlide = slides[targetIndex];
      if (!newSlide) return;

      if (this.#scroll) {
        this.#scroll.to(newSlide);
      } else {
        try {
          newSlide.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        } catch (e) {
          /* ignore */
        }
      }

      this.removeAttribute('dragging');
      this.#centerSelectedThumbnail(targetIndex);
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: targetIndex,
          previousIndex: currentIdx,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );
      this.current = targetIndex;
      await this.#scroll?.finished;
      if (this.#dragging) return;
      if (this.#scroll) this.#scroll.snap = true;
      this.resume();
    };

    if (this.#scroll) this.#scroll.snap = false;
    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  // Read direction attribute + breakpoint to decide axis.
  // If direction="vertical" is present -> vertical on large screens, horizontal on small.
  #updateAxis = (event) => {
    // If the element does not request vertical behavior, always use horizontal
    const wantsVertical = this.hasAttribute('direction') && this.getAttribute('direction') === 'vertical';
    const matchesLarge = event?.matches ?? mediaQueryLarge.matches;
    const newAxis = wantsVertical ? (matchesLarge ? 'y' : 'x') : 'x';

    if (newAxis === this.#axis) return;
    this.#axis = newAxis;

    const scroller = this.scrollerEl;
    const slideshowContainer = this.slideshowContainerEl;
    if (!scroller) return;

    if (this.#axis === 'y') {
      slideshowContainer?.classList.add('slideshow--vertical');
      // ensure this instance's scroller becomes vertical
      this.#applyVertical();
    } else {
      // Explicit horizontal reset (ensure no leftover vertical CSS)
      this.slideshowContainerEl?.classList.remove('slideshow--vertical');
      this.#applyHorizontal();
    }

    // recreate scroller on the chosen element
    this.#createScroller(scroller);
    scroller.removeEventListener('pointerdown', this.#handleMouseDown);
    scroller.addEventListener('pointerdown', this.#handleMouseDown);

    this.#updateVisibleSlides();
    this.#sync();
    this.#updateControlsVisibility();
  };

  #handleScroll = () => {
    const prev = this.#current;
    const idx = this.#sync();
    if (idx === prev) return;
    const slide = this.slides?.[idx];
    if (!slide) return;
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: idx,
        previousIndex: prev,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  // Synchronize scroll position and return current index
  #sync = () => {
    const slides = this.slides;
    if (!slides.length) return (this.current = 0);
    const visible = this.visibleSlides;
    if (!visible.length) return this.current;
    const axis = this.#scroll?.axis || this.#axis;
    const scroller = this.scrollerEl;
    const centers = visible.map((s) => center(s, axis));
    const referencePoint = visible.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visible[centers.indexOf(closestCenter)];
    if (!closestVisibleSlide) return (this.current = 0);
    const index = slides.indexOf(closestVisibleSlide);
    return (this.current = index);
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;
    const scroller = this.scrollerEl;
    const { slideshowControls } = this.refs;
    if (!(slideshowControls instanceof HTMLElement) || !scroller) return;
    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;
    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;
    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;
    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /* -------------------------
     Lifecycle
     ------------------------- */

  async connectedCallback() {
    super.connectedCallback();

    if (viewTransition.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }

    const scroller = this.scrollerEl;
    // If direction="vertical" present -> respect breakpoint; otherwise horizontal by default.
    const wantsVertical = this.hasAttribute('direction') && this.getAttribute('direction') === 'vertical';
    // Force axis explicitly and ensure any leftover vertical classes/styles are removed for horizontal instances.
    this.#axis = wantsVertical ? (mediaQueryLarge.matches ? 'y' : 'x') : 'x';

    if (!scroller) return;
    this.#createScroller(scroller);

    // Apply initial classes/styles for axis
    if (this.#axis === 'y') {
      this.slideshowContainerEl?.classList.add('slideshow--vertical');
      // ensure this instance's scroller becomes vertical
      this.#applyVertical();
    } else {
      // Explicit horizontal reset (ensure no leftover vertical CSS)
      this.slideshowContainerEl?.classList.remove('slideshow--vertical');
      this.#applyHorizontal();
    }

    // ensure pointer listener is attached for dragging (use pointer events)
    scroller.removeEventListener('pointerdown', this.#handleMouseDown);
    scroller.addEventListener('pointerdown', this.#handleMouseDown);

    // Only listen to breakpoint changes if the element requested vertical behaviour
    if (wantsVertical) mediaQueryLarge.addEventListener('change', this.#updateAxis);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Thumbnails delegation: prefer thumbnailsContainer ref
    this.#thumbnailListeners = [];
    const refThumbnails = this.refs.thumbnailsContainer || this.refs.thumbnails;
    const containerSelectorFallback = '[data-slide-index], .slideshow-controls__thumbnails button';

    if (refThumbnails instanceof HTMLElement) {
      const delegatedHandler = (evt) => {
        const target = /** @type {Element} */ (evt.target);
        const button = target.closest('button, a, [data-slide-index]');
        if (!button) return;
        evt.preventDefault();
        const slides = Array.from(this.refs.slides || this.refs.slideshowContainer?.querySelectorAll('slideshow-slide') || []);
        const slideId = button.getAttribute('slide-id') || button.dataset?.slideId || (button.getAttribute('href') || '').split('#')[1];
        let index = -1;
        if (slideId && slides.length) index = slides.findIndex((s) => s.getAttribute('slide-id') === slideId);
        if (index === -1) {
          const dsIdx = button.dataset?.index ?? button.dataset?.slideIndex;
          if (dsIdx != null) index = parseInt(dsIdx, 10);
        }
        if (index === -1) {
          const thumbs = Array.from(refThumbnails.querySelectorAll('button, a, [data-slide-index]'));
          index = thumbs.indexOf(button);
        }
        // debug (remove when confirmed)
        // console.log('slideshow: thumbnail click ->', { index, slideId, slidesLength: slides.length });
        if (!slides.length || index < 0 || index >= slides.length) return;
        this.select(index, evt);
      };
      refThumbnails.addEventListener('click', delegatedHandler);
      this.#thumbnailListeners.push({ el: refThumbnails, handler: delegatedHandler, delegated: true });
    } else {
      // fallback: attach to each thumbnail element
      let thumbs = [];
      if (Array.isArray(this.refs.thumbnails) || (this.refs.thumbnails && this.refs.thumbnails.length)) {
        thumbs = Array.from(this.refs.thumbnails);
      } else if (this.refs.slideshowContainer) {
        thumbs = Array.from(this.refs.slideshowContainer.querySelectorAll(containerSelectorFallback));
      }
      thumbs.forEach((thumb, i) => {
        const handler = (evt) => {
          evt?.preventDefault?.();
          const slides = Array.from(this.refs.slides || this.refs.slideshowContainer?.querySelectorAll('slideshow-slide') || []);
          const slideId = thumb.getAttribute('slide-id') || thumb.dataset?.slideId || (thumb.getAttribute('href') || '').split('#')[1];
          let index = -1;
          if (slideId && slides.length) index = slides.findIndex((s) => s.getAttribute('slide-id') === slideId);
          if (index === -1) {
            const dsIdx = thumb.dataset?.index ?? thumb.dataset?.slideIndex;
            if (dsIdx != null) index = parseInt(dsIdx, 10);
          }
          if (index === -1 || Number.isNaN(index)) index = i;
          if (!slides.length || index < 0 || index >= slides.length) return;
          this.select(index, evt);
        };
        thumb.addEventListener('click', handler);
        this.#thumbnailListeners.push({ el: thumb, handler });
      });
    }

    // Ensure pagination dots navigate slides
    try {
      const dots = Array.isArray(this.refs.dots) ? this.refs.dots : (this.refs.dots ? Array.from(this.refs.dots) : []);
      if (dots && dots.length) {
        dots.forEach((dot, i) => {
          const handler = (ev) => {
            ev?.preventDefault?.();
            this.select(i, ev);
          };
          dot.addEventListener('click', handler);
          this.#thumbnailListeners.push({ el: dot, handler });
        });
      }
    } catch (e) {
      // ignore if refs.dots not present
    }

    // initial visibility / resize observer
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');
      if (this.initialSlideIndex !== 0 && initialSlideId) {
        this.select({ id: initialSlideId }, undefined, { animate: false });
        visibleSlidesAmount = 1;
      } else {
        visibleSlidesAmount = this.#updateVisibleSlides();
        if (visibleSlidesAmount === 0) {
          this.select(0, undefined, { animate: false });
          visibleSlidesAmount = 1;
        }
      }

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;
        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }
        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });
      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const scroller = this.scrollerEl;
    scroller?.removeEventListener('pointerdown', this.#handleMouseDown);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scroll?.destroy();
    // no breakpoint listeners to remove (axis controlled via direction attribute)

    if (this.#thumbnailListeners?.length) {
      this.#thumbnailListeners.forEach(({ el, handler }) => {
        try {
          el.removeEventListener('click', handler);
        } catch (e) {
          // ignore
        }
      });
      this.#thumbnailListeners = [];
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    // Remove mediaQuery listener unconditionally (safe even if not registered)
    try {
      mediaQueryLarge.removeEventListener('change', this.#updateAxis);
    } catch (e) {
      // ignore
    }
  }

  /* -------------------------
     Public API: select / next / previous / autoplay
     ------------------------- */

  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if (input && typeof input === 'object' && 'id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);
        if (!requestedSlide || !this.slides) return;
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }
        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    if (requestedIndex === undefined || Number.isNaN(requestedIndex) || requestedIndex === current) return;

    const slides = this.slides;
    if (!slides.length) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault?.();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const visibleSlides = this.visibleSlides;
    const instant = prefersReducedMotion() || !animate;

    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll?.finished;
      const targetSlide = slides[index];
      const currentSlide = slides[current];
      if (!targetSlide || !currentSlide) return;

      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      if (requestedIndex < current) currentSlide.before(targetSlide);
      else currentSlide.after(targetSlide);

      if (current === 0) this.#scroll?.to(currentSlide, { instant: true });

      queueMicrotask(async () => {
        await this.#scroll?.finished;
        this.#disabled = false;
        placeholder.replaceWith(targetSlide);
        this.#scroll?.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;
    slide.setAttribute('aria-hidden', 'false');

    // Prefer Scroller API when the scroller can actually scroll along the axis;
    // otherwise fall back to native scrollIntoView so thumbnails still navigate
    // to the image when slides are stacked in page flow.
    const scrollerEl = this.scrollerEl;
    const axis = this.#scroll?.axis || this.#axis;
    const scrollerScrollable =
      scrollerEl &&
      ((axis === 'x' && scrollerEl.scrollWidth > scrollerEl.clientWidth) ||
        (axis === 'y' && scrollerEl.scrollHeight > scrollerEl.clientHeight));

    if (this.#scroll && scrollerScrollable) {
      this.#scroll.to(slide, { instant });
    } else {
      // fallback to native scrolling (works when slides are laid out vertically on the page)
      try {
        slide.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'center', inline: 'center' });
      } catch (e) {
        // ignore if scrollIntoView fails for custom elements
      }
    }
    this.current = this.slides?.indexOf(slide) || 0;
    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  next(event, options) {
    event?.preventDefault?.();
    this.select(this.nextIndex, event, options);
  }

  previous(event, options) {
    event?.preventDefault?.();
    this.select(this.previousIndex, event, options);
  }

  play(interval = this.autoplayInterval) {
    if (this.#interval) return;
    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) this.setAttribute('paused', '');
    else this.removeAttribute('paused');
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  /* -------------------------
     attributes / small helpers
     ------------------------- */

  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }

  get disabled() {
    return this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches);
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}